import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Trash2 } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

// Helper to get current date in DD/MM/YYYY format
const getCurrentDate = () => {
  const today = new Date();
  const day = String(today.getDate()).padStart(2, '0');
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const year = today.getFullYear();
  return `${day}/${month}/${year}`;
};

export default function EventsTabEditor({ data, onChange }) {
  const [activeSection, setActiveSection] = useState('attended');

  if (!data) return null;

  const updateField = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  const updateSection = (section, value) => {
    onChange({ ...data, [section]: value });
  };

  // Events Attended
  const addEventAttended = () => {
    const newList = [...(data.attended || []), {
      id: uuidv4(),
      programme_name: '',
      organized_by: '',
      programme_date: getCurrentDate()
    }];
    updateSection('attended', newList);
  };

  const updateEventAttended = (index, field, value) => {
    const newList = [...data.attended];
    newList[index] = { ...newList[index], [field]: value };
    updateSection('attended', newList);
  };

  const removeEventAttended = (index) => {
    const newList = data.attended.filter((_, i) => i !== index);
    updateSection('attended', newList);
  };

  // Events Organised
  const addEventOrganised = () => {
    const newList = [...(data.organised || []), {
      id: uuidv4(),
      programme_name: '',
      event_date: getCurrentDate()
    }];
    updateSection('organised', newList);
  };

  const updateEventOrganised = (index, field, value) => {
    const newList = [...data.organised];
    newList[index] = { ...newList[index], [field]: value };
    updateSection('organised', newList);
  };

  const removeEventOrganised = (index) => {
    const newList = data.organised.filter((_, i) => i !== index);
    updateSection('organised', newList);
  };

  return (
    <div className="space-y-6">
      {/* Title & Description */}
      <div className="bg-white p-6 rounded-lg border space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Section Title
          </label>
          <Input
            value={data.title || ''}
            onChange={(e) => updateField('title', e.target.value)}
            placeholder="Events"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description (HTML)
          </label>
          <Textarea
            value={data.description || ''}
            onChange={(e) => updateField('description', e.target.value)}
            placeholder="<p>Description...</p>"
            rows={4}
          />
        </div>
      </div>

      {/* Section Selector */}
      <div className="bg-white p-4 rounded-lg border">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Select Event Type
        </label>
        <div className="flex gap-2">
          <Button
            onClick={() => setActiveSection('attended')}
            variant={activeSection === 'attended' ? 'default' : 'outline'}
            size="sm"
            className={activeSection === 'attended' ? 'bg-[#0E6D2B]' : ''}
          >
            Training/Workshop/Seminar Attended
          </Button>
          <Button
            onClick={() => setActiveSection('organised')}
            variant={activeSection === 'organised' ? 'default' : 'outline'}
            size="sm"
            className={activeSection === 'organised' ? 'bg-[#0E6D2B]' : ''}
          >
            Training/Workshop/Seminar Organised
          </Button>
        </div>
      </div>

      {/* Events Attended */}
      {activeSection === 'attended' && (
        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Training/Workshop/Seminar Attended</h3>
            <Button onClick={addEventAttended} size="sm">
              <Plus className="w-4 h-4 mr-1" /> Add Event
            </Button>
          </div>
          <div className="space-y-4">
            {data.attended && data.attended.map((event, index) => (
              <div key={event.id} className="border rounded p-4 bg-gray-50">
                <div className="grid md:grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">Programme Name</label>
                    <Input
                      value={event.programme_name}
                      onChange={(e) => updateEventAttended(index, 'programme_name', e.target.value)}
                      placeholder="National Workshop on IPR..."
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">Date (DD/MM/YYYY)</label>
                    <Input
                      value={event.programme_date}
                      onChange={(e) => updateEventAttended(index, 'programme_date', e.target.value)}
                      placeholder="15/03/2024"
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="text-xs text-gray-600 mb-1 block">Organized By</label>
                  <Input
                    value={event.organized_by}
                    onChange={(e) => updateEventAttended(index, 'organized_by', e.target.value)}
                    placeholder="ICAR-NAARM, Hyderabad"
                  />
                </div>
                <Button onClick={() => removeEventAttended(index)} size="sm" variant="destructive">
                  <Trash2 className="w-4 h-4 mr-1" /> Remove Event
                </Button>
              </div>
            ))}
            {(!data.attended || data.attended.length === 0) && (
              <p className="text-gray-500 text-center py-4">No events attended yet. Click &ldquo;Add Event&rdquo; to start.</p>
            )}
          </div>
        </div>
      )}

      {/* Events Organised */}
      {activeSection === 'organised' && (
        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Capacity Building: Training/Workshop/Seminar Organised</h3>
            <Button onClick={addEventOrganised} size="sm">
              <Plus className="w-4 h-4 mr-1" /> Add Event
            </Button>
          </div>
          <div className="space-y-4">
            {data.organised && data.organised.map((event, index) => (
              <div key={event.id} className="border rounded p-4 bg-gray-50">
                <div className="grid md:grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">Programme Name</label>
                    <Input
                      value={event.programme_name}
                      onChange={(e) => updateEventOrganised(index, 'programme_name', e.target.value)}
                      placeholder="Workshop on Technology Licensing..."
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">Date (DD/MM/YYYY)</label>
                    <Input
                      value={event.event_date}
                      onChange={(e) => updateEventOrganised(index, 'event_date', e.target.value)}
                      placeholder="10/03/2024"
                    />
                  </div>
                </div>
                <Button onClick={() => removeEventOrganised(index)} size="sm" variant="destructive">
                  <Trash2 className="w-4 h-4 mr-1" /> Remove Event
                </Button>
              </div>
            ))}
            {(!data.organised || data.organised.length === 0) && (
              <p className="text-gray-500 text-center py-4">No events organised yet. Click &ldquo;Add Event&rdquo; to start.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
